import type { NxHue } from '../lib/hue';
import { chartColor, type NxChartTone } from './series';

/** Key of a segment. Numbers and strings are both fine; they are compared with `===`. */
export type NxDonutKey = string | number;

/** Intent colour of a segment: for a share that means something (overdue = `danger`). */
export type NxDonutTone = NxChartTone;

export interface NxDonutSegment {
  key: NxDonutKey;
  label: string;
  /** Count or amount. Negative and non-finite values count as 0. */
  value: number;
  /**
   * An intent tone when the colour says something (overdue = `danger`), a
   * category hue when it only tells groups apart. Without one the segment takes
   * the hue of its position — pass one wherever colours must stay put while the
   * list changes.
   */
  tone?: NxDonutTone | NxHue;
}

/**
 * CSS colour of a segment: its tone or hue, else the chart-palette slot of its
 * position (`--nxt-chart-*`, whose order keeps neighbours apart for colour-blind
 * readers). A ninth untoned segment repeats the first colour: fold the rest into
 * an „other“ segment instead.
 */
export function donutSegmentColor(tone: NxDonutTone | NxHue | undefined, index: number): string {
  return chartColor(tone, index);
}

/** One drawn arc: a dash on a circle of the given circumference. */
export interface NxDonutArc {
  key: NxDonutKey;
  /** Position of the segment in the list it came from. */
  index: number;
  /** Share of the sum, 0–1. */
  share: number;
  /** Length of the visible dash. */
  length: number;
  /** Where the dash starts, measured along the circle from 12 o'clock, clockwise. */
  start: number;
  color: string;
}

function countable(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/** Sum of the countable values. */
export function donutSum(segments: readonly NxDonutSegment[]): number {
  return segments.reduce((sum, s) => sum + countable(s.value), 0);
}

/**
 * Lays the segments out as dashes on a circle of `circumference`, clockwise
 * from 12 o'clock in list order. Segments without a share get no arc (they
 * still belong in the legend). Between two arcs `gap` stays free, half taken
 * from each side; a sliver shorter than the gap keeps its true length rather
 * than vanishing.
 */
export function layoutDonut(
  segments: readonly NxDonutSegment[],
  circumference: number,
  gap = 0
): NxDonutArc[] {
  const sum = donutSum(segments);
  if (sum <= 0) return [];
  const drawn = segments.filter((s) => countable(s.value) > 0).length;
  const spacing = drawn > 1 ? gap : 0;
  const arcs: NxDonutArc[] = [];
  let offset = 0;
  segments.forEach((segment, index) => {
    const value = countable(segment.value);
    if (!value) return;
    const share = value / sum;
    const full = share * circumference;
    const length = full > spacing ? full - spacing : full;
    arcs.push({
      key: segment.key,
      index,
      share,
      length,
      start: offset + (full - length) / 2,
      color: donutSegmentColor(segment.tone, index),
    });
    offset += full;
  });
  return arcs;
}
